import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip
} from '@mui/material';
import { EmojiEvents as EmojiEventsIcon, Lock as LockIcon, ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { useLevels, LevelConfig } from '../context/LevelContext';
import { useUser } from '../context/UserContext';
import LevelDisplay from '../components/LevelDisplay';

const BADGE_CONFIG: Record<string, { color: string; gradient: string }> = {
  bronze: { color: '#CD7F32', gradient: 'linear-gradient(135deg, #CD7F32 0%, #8B4513 100%)' },
  silver: { color: '#A9A9A9', gradient: 'linear-gradient(135deg, #C0C0C0 0%, #A9A9A9 100%)' },
  gold: { color: '#DAA520', gradient: 'linear-gradient(135deg, #FFD700 0%, #DAA520 100%)' },
  diamond: { color: '#1B7FC4', gradient: 'linear-gradient(135deg, #7DD3F8 0%, #1B7FC4 100%)' },
};

const LevelsPage: React.FC = () => {
  const { levels } = useLevels();
  const { user } = useUser();

  const currentLevel = user?.level ?? 0;
  const currentPoints = user?.totalPoints ?? 0;

  const getNextLevelInfo = () => {
    const nextLevel = levels[currentLevel + 1];
    if (nextLevel) {
      const pointsNeeded = nextLevel.minPoints - currentPoints;
      return {
        name: nextLevel.name,
        pointsNeeded: pointsNeeded > 0 ? pointsNeeded : 0
      };
    }
    return null;
  };

  const nextLevelInfo = getNextLevelInfo();

  // Group levels by badge type
  const groupedLevels = levels.reduce((acc, level, index) => {
    const badge = level.badge;
    if (!acc[badge]) {
      acc[badge] = [];
    }
    acc[badge].push({ ...level, index });
    return acc;
  }, {} as Record<string, (LevelConfig & { index: number })[]>);

  const renderLevelBox = (level: LevelConfig & { index: number }) => {
    const isCurrentLevel = level.index === currentLevel;
    const isAchieved = level.index <= currentLevel;

    return (
      <Box
        key={level.id}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          p: 1.5,
          borderRadius: 2,
          bgcolor: isCurrentLevel ? 'primary.main' : isAchieved ? 'grey.100' : 'transparent',
          border: isCurrentLevel ? 'none' : '1px solid',
          borderColor: isAchieved ? 'grey.200' : 'grey.300',
          opacity: isAchieved ? 1 : 0.5,
          position: 'relative',
          minWidth: 70,
          flexShrink: 0,
        }}
      >
        {isCurrentLevel && (
          <Chip
            label="You"
            size="small"
            sx={{
              position: 'absolute',
              top: -10,
              bgcolor: 'white',
              color: 'primary.main',
              fontWeight: 700,
              fontSize: '0.65rem',
              height: 18,
              boxShadow: 1
            }}
          />
        )}

        {!isAchieved && (
          <LockIcon
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              fontSize: 12,
              color: 'grey.400'
            }}
          />
        )}

        <LevelDisplay level={level.index} size="small" clickable={false} />

        <Typography
          variant="caption"
          sx={{
            color: isCurrentLevel ? 'rgba(255,255,255,0.8)' : 'text.secondary',
            fontSize: '0.7rem'
          }}
        >
          {level.minPoints} pts
        </Typography>
      </Box>
    );
  };

  const renderLevelRow = (badgeLevels: (LevelConfig & { index: number })[]) => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          overflowX: 'auto',
          py: 1,
          '&::-webkit-scrollbar': {
            height: 6,
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'grey.100',
            borderRadius: 3,
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'grey.300',
            borderRadius: 3,
          },
        }}
      >
        {badgeLevels.map((level, idx) => {
          const isLast = idx === badgeLevels.length - 1;
          const isAchieved = level.index < currentLevel;

          return (
            <React.Fragment key={level.id}>
              {renderLevelBox(level)}
              {!isLast && (
                <ArrowForwardIcon
                  sx={{
                    fontSize: 18,
                    color: isAchieved ? 'primary.main' : 'grey.400',
                    flexShrink: 0
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </Box>
    );
  };

  return (
    <Box>
      {/* Current Level Info */}
      {user && nextLevelInfo && (() => {
        const currentMinPts = levels[currentLevel]?.minPoints ?? 0;
        const nextMinPts = levels[currentLevel + 1]?.minPoints ?? currentMinPts;
        const progressPct = Math.min(
          ((currentPoints - currentMinPts) / (nextMinPts - currentMinPts)) * 100,
          100
        );
        return (
          <Card sx={{ mb: 3, borderRadius: 3, background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' }}>
            <CardContent sx={{ py: 2, px: 3 }}>
              {/* Row: current level → progress bar → next level */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <LevelDisplay level={currentLevel} size="large" clickable={false} />
                  <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, mt: 0.5 }}>
                    {levels[currentLevel]?.name || 'Unknown'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {currentPoints} pts
                  </Typography>
                </Box>

                {/* Progress bar in the middle */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#fbbf24', fontWeight: 700 }}>
                      {Math.round(progressPct)}%
                    </Typography>
                  </Box>
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{ height: 14, borderRadius: 7, bgcolor: 'rgba(255,255,255,0.15)', overflow: 'hidden' }}>
                      <Box
                        sx={{
                          height: '100%',
                          width: `${progressPct}%`,
                          borderRadius: 7,
                          background: 'linear-gradient(90deg, #fbbf24 0%, #f59e0b 100%)',
                          transition: 'width 0.5s ease',
                          position: 'relative'
                        }}
                      />
                    </Box>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', textAlign: 'center' }}>
                    {nextLevelInfo.pointsNeeded} more points needed
                  </Typography>
                </Box>

                {/* Next level */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, opacity: 0.6 }}>
                  <LevelDisplay level={currentLevel + 1} size="large" clickable={false} />
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 600, mt: 0.5 }}>
                    {nextLevelInfo.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    {levels[currentLevel + 1]?.minPoints} pts
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        );
      })()}

      {/* Max Level Reached */}
      {user && !nextLevelInfo && (
        <Card sx={{ mb: 3, borderRadius: 3, background: 'linear-gradient(135deg, #FFD700 0%, #DAA520 100%)' }}>
          <CardContent sx={{ py: 2.5, textAlign: 'center' }}>
            <EmojiEventsIcon sx={{ fontSize: 40, color: 'white', mb: 0.5 }} />
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
              Maximum Level Reached!
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              You've achieved the highest level: {levels[currentLevel]?.name}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Levels grouped by badge */}
      {(['bronze', 'silver', 'gold', 'diamond'] as const).map((badge) => {
        const badgeLevels = groupedLevels[badge] || [];
        if (badgeLevels.length === 0) return null;

        const badgeConfig = BADGE_CONFIG[badge];

        return (
          <Card key={badge} sx={{ mb: 2, borderRadius: 3 }}>
            <CardContent sx={{ p: 2 }}>
              {/* Badge header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: badgeConfig.gradient,
                    boxShadow: 1
                  }}
                />
                <Typography
                  variant="subtitle2"
                  sx={{
                    textTransform: 'capitalize',
                    fontWeight: 700,
                    letterSpacing: 1,
                    color: badgeConfig.color
                  }}
                >
                  {badge}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  ({badgeLevels.length} levels)
                </Typography>
              </Box>

              {/* Levels row */}
              {renderLevelRow(badgeLevels)}
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

export default LevelsPage;